import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { buttonVariants } from "@cronika/ui";
import { useContactModal } from "@/core/providers/ContactModalContext";
import { links } from "@/shared/config/site";
import { useContent } from "@/shared/lib/i18n";

function useTelegramChannel() {
  const { contactModal: t } = useContent();
  const isRu = typeof navigator !== "undefined" && navigator.language.startsWith("ru");
  return {
    url: isRu ? links.contact.telegramChannelRu : links.contact.telegramChannelEn,
    handle: "Cronika_Affiliates",
    cta: t.telegram.channel.cta,
  };
}

export function ContactModal() {
  const { open, closeModal } = useContactModal();
  const { contactModal: t } = useContent();
  const channel = useTelegramChannel();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, closeModal]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[100]"
            style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeModal}
          />

          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
            <motion.div
              className="w-full max-w-[680px]"
              initial={{ opacity: 0, scale: 0.96, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 10 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="relative rounded-2xl px-5 py-8 sm:px-[20px] sm:py-10"
                style={{
                  background: "rgba(140,140,140,0.15)",
                  backdropFilter: "blur(20px)",
                  WebkitBackdropFilter: "blur(20px)",
                }}
              >
                <button
                  onClick={closeModal}
                  className="absolute top-5 right-5 flex items-center justify-center w-8 h-8 text-white/50 hover:text-white transition-colors"
                  aria-label={t.closeAriaLabel}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M1 1L15 15M15 1L1 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>

                <div className="text-center mb-6 sm:mb-8">
                  <h2 className="text-2xl sm:text-3xl font-medium text-white mb-3">{t.title}</h2>
                  <p className="text-base sm:text-xl text-white/55 leading-relaxed mx-auto">{t.subtitle}</p>
                </div>

                <div className="flex flex-col gap-3">
                  {/* Telegram manager */}
                  <div
                    className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl px-5 py-4"
                    style={{ background: "rgba(255,255,255,0.05)" }}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div
                        className="shrink-0 rounded-full flex items-center justify-center"
                        style={{
                          width: 50,
                          height: 50,
                          padding: "14px 12px",
                          background: "rgba(140,140,140,0.15)",
                          border: "1px solid rgba(140,140,140,0.15)",
                        }}
                      >
                        <img src="/icons/yellow_telegram.svg" width={20} height={17} alt="" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">{t.telegram.manager.title}</p>
                        <a
                          href={links.contact.telegramManager}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium"
                          style={{ color: "#fec201" }}
                        >
                          {links.contact.telegramManagerHandle}
                        </a>
                      </div>
                    </div>
                    <a
                      href={links.contact.telegramManager}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={buttonVariants({ variant: "glow", size: "sm", radius: "xl", className: "shrink-0 w-full sm:w-auto justify-center" })}
                    >
                      {t.telegram.manager.cta}
                    </a>
                  </div>

                  {/* Email */}
                  <div
                    className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl px-5 py-4"
                    style={{ background: "rgba(255,255,255,0.05)" }}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div
                        className="shrink-0 rounded-full flex items-center justify-center"
                        style={{
                          width: 50,
                          height: 50,
                          padding: "14px 12px",
                          background: "rgba(140,140,140,0.15)",
                          border: "1px solid rgba(140,140,140,0.15)",
                        }}
                      >
                        <img src="/icons/yellow_email.svg" width={20} height={20} alt="" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">{t.email.title}</p>
                        <a href={links.contact.email} className="text-xs font-medium" style={{ color: "#fec201" }}>
                          {links.contact.emailHandle}
                        </a>
                      </div>
                    </div>
                    <a
                      href={links.contact.email}
                      className="shrink-0 w-full sm:w-auto inline-flex items-center justify-center h-9 px-5 rounded-full text-sm font-semibold border transition-all hover:opacity-80"
                      style={{ borderColor: "#fec201", color: "#fec201" }}
                    >
                      {t.email.cta}
                    </a>
                  </div>

                  {/* Telegram channel */}
                  <div
                    className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl px-5 py-4"
                    style={{ background: "rgba(255,255,255,0.05)" }}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div
                        className="shrink-0 rounded-full flex items-center justify-center"
                        style={{
                          width: 50,
                          height: 50,
                          padding: "14px 12px",
                          background: "rgba(140,140,140,0.15)",
                          border: "1px solid rgba(140,140,140,0.15)",
                        }}
                      >
                        <img src="/icons/yellow_telegram.svg" width={20} height={17} alt="" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white">{t.telegram.channel.title}</p>
                        <a
                          href={channel.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs font-medium"
                          style={{ color: "#fec201" }}
                        >
                          {channel.handle}
                        </a>
                      </div>
                    </div>
                    <a
                      href={channel.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 w-full sm:w-auto inline-flex items-center justify-center h-9 px-5 rounded-full text-sm font-semibold border transition-all hover:opacity-80"
                      style={{ borderColor: "#fec201", color: "#fec201" }}
                    >
                      {channel.cta}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
